'use client';
import { CartHorizontalScroll } from './CartHorizontalScroll';
import { useEffect, useRef, useState, type ElementType } from 'react';
import {
  formatCartMilestoneCurrency,
  isCartRewardProductEligible,
  prioritizeAlmostThereGroups,
  type CartDiscoveryPresentation,
  type CartRewardGroup,
  type Currency,
} from '@skin-seoul/service';
import { useCartDiscovery } from './CartDiscoveryNavigation';
import { CartRewardOfferCard, CartRewardProductCard } from './CartRewardProductCard';
import { CartBenefitsBanner } from './CartBenefitsBanner';
import { useCartAnalytics } from './CartAnalytics';

const REFRESHING_OPACITY = 0.5;

interface Props {
  currency: Currency;
  linkComponent?: ElementType;
  onProductClick: () => void;
  onCartMutation?: (mutation: Promise<boolean>) => void;
}
function groupLabel(group: CartRewardGroup, currency: Currency) {
  if (group.type === 'almost_there') {
    return 'Almost There';
  }
  if (group.reward.type === 'free_shipping') {
    return 'Unlocks Free Ship';
  }
  if (group.reward.discountType === 'percent') {
    return `Unlocks ${group.reward.percentage}% OFF`;
  }
  return `Unlocks ${formatCartMilestoneCurrency(currency, group.reward.discountAmount)} OFF`;
}
export function CartRewardDiscovery(props: Props & { initialGroup: string }) {
  const { track } = useCartAnalytics();
  const viewed = useRef(false);
  const { presentation, back, isRefreshing, isInitialLoading } = useCartDiscovery();
  const groups = prioritizeAlmostThereGroups(
    presentation?.groups.filter((group) => group.products.some(isCartRewardProductEligible)) ?? [],
  );
  const [selected, setSelected] = useState(
    groups.find((group) => group.type === 'almost_there')?.key ?? props.initialGroup,
  );
  const current = groups.find((group) => group.key === selected) ?? groups[0];
  useEffect(() => {
    if (!current || viewed.current) {
      return;
    }
    viewed.current = true;
    track(
      'unlock_reward_page_viewed',
      { tab: current.key, available_tabs: groups.map(({ key }) => key) },
      'unlock_reward_page',
    );
  }, [current, groups, track]);
  const selectTab = (tab: string) => {
    if (current?.key !== tab) {
      track('unlock_reward_tab_clicked', { tab, previous_tab: current?.key }, 'unlock_reward_page');
    }
    setSelected(tab);
  };
  useEffect(() => {
    if (!current && !isRefreshing && !isInitialLoading) {
      back('no_products');
    }
  }, [current, isRefreshing, isInitialLoading, back]);
  if (!current) {
    return null;
  }
  return (
    <>
      <div className="sticky top-0 z-sticky bg-white px-4 py-5">
        <CartBenefitsBanner isTrackingActive uiLocation="unlock_reward_page" />
      </div>
      <section
        inert={isRefreshing}
        aria-busy={isRefreshing}
        style={{ opacity: isRefreshing ? REFRESHING_OPACITY : 1 }}
        className="border-t-8 border-neutral-50 px-4 pt-4 pb-6"
      >
        <h3 className="text-label-large leading-6 font-semibold text-neutral-700">Closest to your reward</h3>
        <p className="mt-1.5 text-label-small text-neutral-400">*price after the reward this unlocks</p>
        <div
          role="tablist"
          aria-label="Reward"
          className="-mx-4 mt-1.5 mb-4 flex gap-2 overflow-x-auto px-4 py-3 scrollbar-none"
        >
          {groups.map((group) => (
            <button
              key={group.key}
              type="button"
              role="tab"
              aria-selected={group.key === current.key}
              tabIndex={group.key === current.key ? 0 : -1}
              onKeyDown={(event) => {
                const index = groups.findIndex((candidate) => candidate.key === group.key);
                const nextIndex = (
                  {
                    ArrowRight: (index + 1) % groups.length,
                    ArrowLeft: (index + groups.length - 1) % groups.length,
                    Home: 0,
                    End: groups.length - 1,
                  } as Record<string, number>
                )[event.key];
                if (nextIndex === undefined) {
                  return;
                }
                event.preventDefault();
                selectTab(groups[nextIndex].key);
                const next =
                  event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex];
                next?.focus();
                next?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
              }}
              onClick={(event) => {
                selectTab(group.key);
                event.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' });
              }}
              className={`h-9 shrink-0 rounded-full border px-4 text-label-medium ${group.key === current.key ? 'border-primary-500 bg-primary-500 text-white' : 'border-neutral-100 bg-white text-neutral-300'}`}
            >
              {groupLabel(group, props.currency)}
            </button>
          ))}
        </div>
        <div
          role="tabpanel"
          aria-label={groupLabel(current, props.currency)}
          className="grid grid-cols-2 gap-x-3 gap-y-4"
        >
          {current.products.map((offer, index) => (
            <CartRewardProductCard
              key={`${offer.projection.sku}-${index}`}
              offer={offer}
              {...props}
              onProductClick={() => {
                back('product_click');
                props.onProductClick();
              }}
              attribution={{ placement: 'unlock_reward_page', position: index, tab: current.key, isDetail: true }}
            />
          ))}
        </div>
      </section>
    </>
  );
}
export function getVisibleCartRewardGroup(presentation?: CartDiscoveryPresentation) {
  return prioritizeAlmostThereGroups(presentation?.groups ?? []).find((candidate) =>
    candidate.products.some(isCartRewardProductEligible),
  );
}
export function CartRewardUpsell({ group, ...props }: Props & { group: CartRewardGroup | undefined }) {
  const { isRefreshing } = useCartDiscovery();
  if (!group) {
    return null;
  }
  return (
    <section
      inert={isRefreshing}
      aria-busy={isRefreshing}
      aria-label="Reward offers"
      className="-mx-4 -mb-3 mt-1 overflow-hidden bg-white"
    >
      <CartHorizontalScroll compact className="flex gap-2.5 overflow-x-auto px-4 py-3 scrollbar-none">
        {group.products.slice(0, 3).map((offer, index) => (
          <CartRewardOfferCard
            key={offer.projection.sku}
            offer={offer}
            {...props}
            attribution={{ placement: 'bag_reward_card', position: index }}
          />
        ))}
      </CartHorizontalScroll>
    </section>
  );
}
